<template>
    <!-- 如果globalModel.preview，那么require无须生效
        readonly借用此属性，如果预览开启，目前只读
    -->
    <image-upload
        :class="{'video-icon': showVideoIcon}"
        :name="`${fileItem.fileId}`"
        :value="fileValue"
        :preview="previewImg"
        :valueImg="valueImg"
        :readonly="readonly"
        :isRequired="!uploadComponent.globalModel.preview && props.required"
        :error="error"
        :tip-error="i18n(props.errNetwork) || error && i18n(error.message)"
        :tip-retry="i18n(props.tipRetry)"
        :is-uploading="isUploading"
        @preview="onUploadPreview"
        @select="onUploadSelect"
        @remove="onUploadClear"
    ></image-upload>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep';
import ImageUpload from '@ks/sharp-comp-image-upload';
import '@ks/sharp-comp-image-upload/lib/themes/default/index.css';
import {
    getValueByPath,
    IMAGE_FILE_TYPES,
    UPLOAD_FILE_PLACEHOLDER_SRC,
    composeUrlWithParam,
} from '../../utils';
import { $fileUploader } from '../component-base/xhr';
import { FILE_STATUS } from './config';
import formEventBus, { FORM_EVENTS_TYPE } from '../../utils/event-bus';

export default {
    name: 'upload-item',
    components: {
        ImageUpload,
    },
    model: {
        prop: 'value',
        event: 'status-change',
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
    },
    inject: ['uploadComponent'],
    data() {
        return {
            fileItem: cloneDeep(this.value),
            valueImgFile: '',
            isUploading: false,
            error: null,
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                this.fileItem = cloneDeep(val);
            },
        },
        'fileItem.status': {
            immediate: true,
            handler(val) {
                // 规定当文件状态为ready时表示刚选中文件需要处理
                if (val === FILE_STATUS.ready) {
                    this.onFileSelect(this.fileItem.value);
                }
            },
        },
        error: {
            handler(e) {
                if (e) {
                    this.fileItem.status = FILE_STATUS.error;
                    this.$emit('status-change', this.fileItem);
                    this.$emit('upload-error', this.fileItem);
                }
            },
        },
    },
    created() {
        this._fileUploader = null;
    },
    computed: {
        config() {
            return this.uploadComponent.config;
        },
        props() {
            return this.uploadComponent.props;
        },
        i18n() {
            return this.uploadComponent.i18n;
        },
        extraParams() {
            return this.uploadComponent.globalModel.globalParams ? {
                ...this.uploadComponent.globalModel.globalParams,
            } : {
                ...this.$route.params,
                ...this.$route.query,
            };
        },
        previewImg() {
            return this.isUploading || this.error ? this.valueImgFile : '';
        },
        fileValue() {
            if (typeof this.fileItem.value === 'string') {
                return this.fileItem.value;
            }
            return this.fileItem.value && this.fileItem.value.name || '';
        },
        valueImg() {
            if (!this.fileItem.value) {
                return '';
            }
            if (this.uploadComponent.formRoot.uploadPreviewMethod) {
                return this.uploadComponent.formRoot.uploadPreviewMethod(
                    this.fileItem.value,
                    this.config,
                );
            }
            if (!this.props.autoUpload) {
                return this.valueImgFile;
            }
            if (this.props.previewSrc) {
                const url = composeUrlWithParam(this.props.previewSrc + '&from={from}&type={type}', {
                    ...this.extraParams,
                    ...this.uploadComponent.globalModel.formData,
                    file: this.fileItem.value,
                    type: this.uploadComponent.globalModel.viewType || 0,
                });
                // 附加参数逻辑同API组件
                return url;
            }
            return UPLOAD_FILE_PLACEHOLDER_SRC;
        },
        readonly() {
            return this.uploadComponent.notChanged || this.uploadComponent.globalModel.preview || this.uploadComponent.globalModel.hideSubmitStyle;
        },
        showVideoIcon() {
            const videoExtensions = /\.(mp4|mpg|mpeg|mov)$/i;
            return videoExtensions.test(this.fileValue) && this.fileItem.status === FILE_STATUS.success;
        },
    },
    methods: {
        async readFileImg(file) {
            const reader = new Promise(resolve => {
                const fileReader = new FileReader();
                fileReader.onload = function (e) {
                    resolve(e.target.result);
                };
                fileReader.readAsDataURL(file);
            });
            this.valueImgFile = await reader;
        },
        onUploadPreview() {
            if (this.showVideoIcon) {return;}
            this.$emit('upload-preview', this.valueImg);
        },
        updatePreview(file) {
            if (!file) {
                return;
            }
            if (IMAGE_FILE_TYPES.includes(file.type)) {
                this.readFileImg(file);
                return;
            }
            this.valueImgFile = '';
        },
        onUploadSelect() {
            // 不在当前组件做文件选择
            // 删除当前文件并抛出事件
            this.$emit('retry-select');
            this.onUploadClear();
        },
        onUploadClear() {
            if (this._fileUploader) {
                this._fileUploader._aborted = true;
            }
            this.fileItem.value = null;
            this.isUploading = false;
            this.error = null;
            this.onFileClear();
        },
        onBeforeUpload(file) {
            // 检查是否超过大小限制
            if (!this.props.maxSize) {
                return !!file;
            }
            // 最大值单位是kb
            const sizeOk = file.size / 1024 <= this.props.maxSize;
            if (!sizeOk) {
                this.setError(this.i18n(this.props.errSizeExceed) || '超出大小限制');
            } else {
                this.setError();
            }
            return sizeOk;
        },
        setAndSyncStatus(status) {
            this.fileItem.status = status;
            this.$emit('status-change', this.fileItem);
            if (status === FILE_STATUS.success) {
                this.$emit('upload-success', this.fileItem);
            }
        },
        async onFileSelect(file) {
            if (!this.onBeforeUpload(file)) {
                return;
            }
            // console.log(file);

            // 上报用户选择的文件信息
            const logFileInfo = {
                name: file.name,
                size: file.size,
                type: file.type,
            };
            this.uploadComponent.logger('upload-select', logFileInfo);

            this.updatePreview(file);
            if (this.props.autoUpload) {
                const uploader = this._fileUploader = this.uploadFile(file);
                this.isUploading = true;
                this.setAndSyncStatus(FILE_STATUS.progress);
                try {
                    const data = await uploader;
                    if (uploader._aborted) {
                        return;
                    }
                    this.isUploading = false;
                    if (this.props.pickKey) {
                        this.fileItem.value = getValueByPath(data, this.props.pickKey);
                    } else {
                        this.fileItem.value = data;
                    }
                    formEventBus.$emit(FORM_EVENTS_TYPE.SEND_FORM_LOG, 'CUSTOM', 'UPLOAD_FILE_SUCCESS', {
                        fileKey: this.fileItem.value,
                    });
                    this.error = null;
                    this.setAndSyncStatus(FILE_STATUS.success);
                    // 上报成功
                    this.uploadComponent.logger('upload-success', {
                        ...logFileInfo,
                        fileKey: this.fileItem.value,
                    });
                } catch (e) {
                    formEventBus.$emit(FORM_EVENTS_TYPE.SEND_FORM_LOG, 'CUSTOM', 'UPLOAD_FILE_FAILED', {
                        error_msg: String(e),
                    });
                    if (uploader._aborted) {
                        return;
                    }
                    this.isUploading = false;
                    this.error = e;
                    this.setAndSyncStatus(FILE_STATUS.error);
                    // 上报失败
                    this.uploadComponent.logger('upload-fail', logFileInfo);
                    return;
                }
            } else {
                this.fileItem.value = file;
                this.setAndSyncStatus(FILE_STATUS.success);
            }
        },
        onFileClear() {
            this.$emit('upload-clear', this.fileItem);
        },
        async uploadFile(file) {
            // console.log(file);
            let data;
            if (typeof this.uploadComponent.formRoot.handleUploadAction === 'function') {
                // 使用外部传入的上传方法
                this._fileUploader = this.uploadComponent.formRoot.handleUploadAction(
                    file,
                    this.config
                );
                data = await this._fileUploader;
            } else {
                // 默认的上传方法
                const url = composeUrlWithParam(this.props.action, {
                    ...this.extraParams,
                    ...this.uploadComponent.globalModel.formData,
                });
                const payload = new FormData();
                payload.append(this.props.fileName, file);
                payload.append('from', this.extraParams.from);

                this._fileUploader = $fileUploader.post(
                    url,
                    payload
                );
                data = (await this._fileUploader).data;
                if (!data || 1 !== data.result) {
                    // i18n在传入属性的时候调用，这里忽略
                    const errMsg = data.error_msg || this.props.errNetwork || '网络错误';
                    throw new Error(errMsg);
                }
            }
            return data;
        },
        setError(msg) {
            if (msg) {
                this.error = new Error(msg);
            } else {
                this.error = null;
            }
        },
    },
};
</script>
<style lang="less" scoped>

.video-icon {
    /deep/ .box {
        position: relative;
        &::before {
            content: '';
            position: absolute;
            z-index: 1;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 24px;
            height: 24px;
            background-size: 100% 100%;
            background: var(--csc-collect-video-icon);
        }
    }
}
</style>
